import {
  AccountInfo,
  AddressLookupTableAccount,
  Commitment,
  ComputeBudgetProgram,
  Connection,
  PublicKey,
  SystemProgram,
  TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js'
import {
  createAssociatedTokenAccountIdempotentInstruction,
  createCloseAccountInstruction,
  createSyncNativeInstruction,
  getAssociatedTokenAddressSync,
  TOKEN_2022_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token'
import { WRAPPED_SOL_MINT } from './constants'
import { getFeeEstimate } from './priorityFee'

export const AUTOBAHN_PROGRAM_ID = process.env.NEXT_AUTOBAHN_PROGRAM_ID
  ? new PublicKey(process.env.NEXT_AUTOBAHN_PROGRAM_ID)
  : new PublicKey('AutobNFLMzX1rFCDgwWpwr3ztG5c1oDbSrGq7Jj2LgE')

export const PLATFORM_FEE_WALLET = process.env.NEXT_PLATFORM_FEE_WALLET
  ? new PublicKey(process.env.NEXT_PLATFORM_FEE_WALLET)
  : new PublicKey('GzYCGGSie66tmTpCpDMu3JHXvTy46rLKNVC6uS4TQgaL')

export const getReferrerTokenAccountForMint = (
  referrer: PublicKey,
  mint: PublicKey,
) => {
  const [tokenAccountPk, tokenAccountBump] = PublicKey.findProgramAddressSync(
    [Buffer.from('referrer'), referrer.toBuffer(), mint.toBuffer()],
    AUTOBAHN_PROGRAM_ID,
  )

  return {
    tokenAccountPk,
    tokenAccountBump,
  }
}

export function createReferrerVaultIx(
  payer: PublicKey,
  referrer: PublicKey,
  vault: PublicKey,
  mint: PublicKey,
  vaultBump: number,
  tokenProgramId: PublicKey = TOKEN_PROGRAM_ID,
) {
  const keys = [
    {
      pubkey: payer,
      isWritable: true,
      isSigner: true,
    },
    {
      pubkey: referrer,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: vault,
      isWritable: true,
      isSigner: false,
    },
    {
      pubkey: mint,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: SystemProgram.programId,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: tokenProgramId,
      isWritable: false,
      isSigner: false,
    },
  ]

  return new TransactionInstruction({
    keys,
    programId: AUTOBAHN_PROGRAM_ID,
    data: Buffer.from([5, vaultBump]),
  })
}

export function createChargeFeeIx(
  payer: PublicKey,
  payerAta: PublicKey,
  platformFeeAta: PublicKey,
  feeAmount: number,
  platformFeePct: number,
  referrer?: PublicKey,
) {
  const keys = [
    {
      pubkey: TOKEN_PROGRAM_ID,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: payerAta,
      isWritable: true,
      isSigner: false,
    },
    {
      pubkey: platformFeeAta,
      isWritable: true,
      isSigner: false,
    },
    {
      pubkey: payer,
      isWritable: false,
      isSigner: true,
    },
  ]

  if (referrer) {
    keys.push({
      pubkey: referrer,
      isWritable: true,
      isSigner: false,
    })
  }

  return new TransactionInstruction({
    keys,
    programId: AUTOBAHN_PROGRAM_ID,
    data: buildFeeIxData(4, feeAmount, platformFeePct),
  })
}

export function createChargeFeeIxV2(
  payer: PublicKey,
  payerAta: PublicKey,
  mintAccount: PublicKey,
  platformFeeAta: PublicKey,
  feeAmount: number,
  platformFeePct: number,
  referrer?: PublicKey,
) {
  const keys = [
    {
      pubkey: TOKEN_2022_PROGRAM_ID,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: mintAccount,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: payerAta,
      isWritable: true,
      isSigner: false,
    },
    {
      pubkey: platformFeeAta,
      isWritable: true,
      isSigner: false,
    },
    {
      pubkey: payer,
      isWritable: false,
      isSigner: true,
    },
  ]

  if (referrer) {
    keys.push({
      pubkey: referrer,
      isWritable: true,
      isSigner: false,
    })
  }

  return new TransactionInstruction({
    keys,
    programId: AUTOBAHN_PROGRAM_ID,
    data: buildFeeIxData(7, feeAmount, platformFeePct),
  })
}

function buildFeeIxData(
  instructionNumber: number,
  feeAmount: number,
  platformFeePct: number,
): Buffer {
  const instruction = instructionNumber

  // Buffer allocation for the resulting byte array
  // 1 byte for instruction, 8 bytes for feeAmount (u64), 1 byte for platform_fee_pct (u8)
  const buffer = Buffer.alloc(1 + 8 + 1)

  let offset = 0

  // Add the instruction as a u8
  buffer.writeUInt8(instruction, offset)
  offset += 1

  // Add the fee amount as a little-endian u64
  buffer.writeBigUInt64LE(BigInt(Math.round(feeAmount)), offset)
  offset += 8

  // Add the platform fee percentage as a u8
  buffer.writeUInt8(platformFeePct, offset)

  return buffer
}

export const tryGetNumber = (val: string) => {
  try {
    return Number(val)
  } catch (e) {
    return null
  }
}

export const enchantVtxWithFees = async (
  vTx: VersionedTransaction,
  walletPk: PublicKey,
  referrerVault:
    | {
        account: AccountInfo<Buffer> | null
        tokenAccountPk: PublicKey
        referrer: string
        referrerFee: number
      }
    | undefined,
  connection: Connection,
  fee: number,
  feeMint: PublicKey,
  feeTokenProgram: PublicKey = TOKEN_PROGRAM_ID,
  isPyth = false,
) => {
  const addressLookupTableAccounts = await Promise.all(
    vTx.message.addressTableLookups.map(async (lookup) => {
      const lookupTableAccountDataBuffer = await connection.getAccountInfo(
        lookup.accountKey,
      )
      const lookupTableAccountData = Uint8Array.from(
        lookupTableAccountDataBuffer!.data,
      )
      return new AddressLookupTableAccount({
        key: lookup.accountKey,
        state: AddressLookupTableAccount.deserialize(lookupTableAccountData),
      })
    }),
  )

  const feeInstructions: TransactionInstruction[] = []
  const newVtx = new VersionedTransaction(vTx.message)

  const inMintIsWsol = feeMint.equals(WRAPPED_SOL_MINT)
  const hasRef = !!referrerVault?.referrer
  let refVaultExists =
    referrerVault?.account && referrerVault.account.lamports > 0

  //ref always gets full fee even if its after rebate
  const platformFeePct = 100 - (referrerVault?.referrerFee ?? 0)

  //double check
  if (!refVaultExists && hasRef) {
    const refVaultAccount = await connection.getAccountInfo(
      referrerVault!.tokenAccountPk,
    )
    refVaultExists = refVaultAccount && refVaultAccount.lamports > 0
  }

  const payerAta = getAssociatedTokenAddressSync(
    feeMint,
    walletPk,
    true,
    feeTokenProgram,
  )
  const platformAta = getAssociatedTokenAddressSync(
    feeMint,
    PLATFORM_FEE_WALLET,
    true,
    feeTokenProgram,
  )
  const ataAccount = await connection.getAccountInfo(platformAta)

  const doesPlatformAtaExists = ataAccount && ataAccount.lamports > 0

  const chargeFee = feeTokenProgram.equals(TOKEN_2022_PROGRAM_ID)
    ? createChargeFeeIxV2(
        walletPk,
        payerAta,
        feeMint,
        platformAta,
        fee,
        platformFeePct,
        referrerVault?.referrer
          ? new PublicKey(referrerVault.tokenAccountPk)
          : undefined,
      )
    : createChargeFeeIx(
        walletPk,
        payerAta,
        platformAta,
        fee,
        platformFeePct,
        referrerVault?.referrer
          ? new PublicKey(referrerVault.tokenAccountPk)
          : undefined,
      )

  const message = TransactionMessage.decompile(newVtx.message, {
    addressLookupTableAccounts,
  })

  // Held so we can drop it if the merged tx would overflow the 1232-byte cap;
  // referrer just doesn't get paid this swap, vault gets created on the next.
  let skippableReferrerIx: TransactionInstruction | null = null
  if (hasRef && !refVaultExists) {
    const referrerVaultIx = createAssociatedTokenAccountIdempotentInstruction(
      walletPk,
      referrerVault!.tokenAccountPk,
      new PublicKey(referrerVault!.referrer),
      feeMint,
      feeTokenProgram,
    )
    skippableReferrerIx = referrerVaultIx
    feeInstructions.push(referrerVaultIx)
    message.instructions.push(referrerVaultIx)
  }
  if (!doesPlatformAtaExists) {
    const platformAtaCreateIx =
      createAssociatedTokenAccountIdempotentInstruction(
        walletPk,
        platformAta,
        PLATFORM_FEE_WALLET,
        feeMint,
        feeTokenProgram,
      )
    feeInstructions.push(platformAtaCreateIx)
    message.instructions.push(platformAtaCreateIx)
  }
  if (inMintIsWsol) {
    const createPayerAtaIx = createAssociatedTokenAccountIdempotentInstruction(
      walletPk,
      payerAta,
      walletPk,
      feeMint,
      feeTokenProgram,
    )
    const solTransferIx = SystemProgram.transfer({
      fromPubkey: walletPk,
      toPubkey: payerAta,
      lamports: fee,
    })
    const syncNative = createSyncNativeInstruction(payerAta)
    feeInstructions.push(createPayerAtaIx, solTransferIx, syncNative)
    message.instructions.push(createPayerAtaIx, solTransferIx, syncNative)
  }
  feeInstructions.push(chargeFee)
  message.instructions.push(chargeFee)

  if (inMintIsWsol) {
    const solTransferIx = createCloseAccountInstruction(
      payerAta,
      walletPk,
      walletPk,
    )
    feeInstructions.push(solTransferIx)
    message.instructions.push(solTransferIx)
  }

  const MAX_TX_SIZE = 1232

  // Measure before sim: getSimulationComputeUnits wraps the same instructions
  // in its own VersionedTransaction, so if the compiled message already
  // overflows the wire cap, the sim will throw "encoding overruns" inside.
  // Probe with a cheap compile+serialize, drop the optional referrer-ATA-create
  // if it tips us over, and fall through to the split path otherwise.
  const measureSerializedLength = (): number => {
    try {
      return new VersionedTransaction(
        message.compileToV0Message(addressLookupTableAccounts),
      ).serialize().length
    } catch (e) {
      if (String(e).includes('encoding overruns')) return 1600
      throw e
    }
  }

  let willOverflow = measureSerializedLength() > MAX_TX_SIZE
  if (willOverflow && skippableReferrerIx) {
    const target = skippableReferrerIx
    const dropFrom = (arr: TransactionInstruction[]) => {
      const i = arr.indexOf(target)
      if (i >= 0) arr.splice(i, 1)
    }
    dropFrom(message.instructions)
    dropFrom(feeInstructions)
    skippableReferrerIx = null
    willOverflow = measureSerializedLength() > MAX_TX_SIZE
  }

  if (!willOverflow) {
    const units = await getSimulationComputeUnits(
      connection,
      message.instructions,
      walletPk,
      addressLookupTableAccounts,
    )
    //replace compute unit with dynamic value
    message.instructions = message.instructions.map((ix) => {
      if (isSetComputeLimitInstruction(ix)) {
        return ComputeBudgetProgram.setComputeUnitLimit({
          units: units || 1_000_000,
        })
      }
      return ix
    })
  }

  newVtx.message = message.compileToV0Message(addressLookupTableAccounts)

  // If the transaction is too large, we need to split it into two transactions
  if (willOverflow || isPyth) {
    const decompiledVtx = TransactionMessage.decompile(vTx.message, {
      addressLookupTableAccounts,
    })

    // Even the bare swap (no fee ixs) can overflow for fat token-2022 routes;
    // skip its sim in that case instead of logging "encoding overruns".
    // Fallback CU (1_000_000) kicks in below.
    let splitSwapLength = 0
    try {
      splitSwapLength = new VersionedTransaction(
        decompiledVtx.compileToV0Message(addressLookupTableAccounts),
      ).serialize().length
    } catch (e) {
      if (String(e).includes('encoding overruns')) splitSwapLength = 1600
      else throw e
    }

    const [unitsVtx, unitsFeeVtx, txFee] = await Promise.all([
      splitSwapLength > MAX_TX_SIZE
        ? Promise.resolve(null)
        : getSimulationComputeUnits(
            connection,
            decompiledVtx.instructions,
            walletPk,
            addressLookupTableAccounts,
          ),
      getSimulationComputeUnits(
        connection,
        feeInstructions,
        walletPk,
        addressLookupTableAccounts,
      ),
      getFeeEstimate(connection),
    ])

    decompiledVtx.instructions = [
      ...decompiledVtx.instructions.map((ix) => {
        if (isSetComputeLimitInstruction(ix)) {
          return ComputeBudgetProgram.setComputeUnitLimit({
            units: unitsVtx || 1_000_000,
          })
        }
        return ix
      }),
    ]
    const compiledVtxMessage = new VersionedTransaction(
      decompiledVtx.compileToV0Message(addressLookupTableAccounts),
    )

    const feeVtx = new VersionedTransaction(
      new TransactionMessage({
        instructions: [
          ComputeBudgetProgram.setComputeUnitLimit({
            units: unitsFeeVtx || 1_400_000,
          }),
          ComputeBudgetProgram.setComputeUnitPrice({
            microLamports: txFee || 60000,
          }),
          ...feeInstructions,
        ],
        payerKey: walletPk,
        recentBlockhash: vTx.message.recentBlockhash,
      }).compileToV0Message(addressLookupTableAccounts),
    )

    return isPyth ? [vTx, feeVtx] : [compiledVtxMessage, feeVtx]
  }

  return [newVtx]
}

export function createWithdrawFeeIx(
  wallet: PublicKey,
  vault: PublicKey,
  mint: PublicKey,
  userAta: PublicKey,
  vaultBump: number,
) {
  const keys = [
    {
      pubkey: wallet,
      isWritable: true,
      isSigner: true,
    },
    {
      pubkey: vault,
      isWritable: true,
      isSigner: false,
    },
    {
      pubkey: mint,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: userAta,
      isWritable: true,
      isSigner: false,
    },
    {
      pubkey: SystemProgram.programId,
      isWritable: false,
      isSigner: false,
    },
    {
      pubkey: TOKEN_PROGRAM_ID,
      isWritable: false,
      isSigner: false,
    },
  ]

  return new TransactionInstruction({
    keys,
    programId: AUTOBAHN_PROGRAM_ID,
    data: Buffer.from([6, vaultBump]),
  })
}

export const getSimulationComputeUnits = async (
  connection: Connection,
  instructions: TransactionInstruction[],
  payer: PublicKey,
  lookupTables: AddressLookupTableAccount[],
  commitment: Commitment = 'confirmed',
): Promise<number | null> => {
  try {
    const simulationInstructions = [...instructions]

    // Replace or add compute limit instruction
    const computeLimitIndex = simulationInstructions.findIndex(
      isSetComputeLimitInstruction,
    )
    const simulationLimitIx = ComputeBudgetProgram.setComputeUnitLimit({
      units: 1_400_000,
    })

    if (computeLimitIndex >= 0) {
      simulationInstructions[computeLimitIndex] = simulationLimitIx
    } else {
      simulationInstructions.unshift(simulationLimitIx)
    }

    const testTransaction = new VersionedTransaction(
      new TransactionMessage({
        instructions: simulationInstructions,
        payerKey: payer,
        // RecentBlockhash can be any public key during simulation
        // since 'replaceRecentBlockhash' is set to 'true' below
        recentBlockhash: PublicKey.default.toString(),
      }).compileToV0Message(lookupTables),
    )

    const rpcResponse = await connection.simulateTransaction(testTransaction, {
      replaceRecentBlockhash: true,
      sigVerify: false,
      commitment,
    })
    const backupAdditionalComputeUnits = 200_000
    return rpcResponse.value.unitsConsumed
      ? rpcResponse.value.unitsConsumed + backupAdditionalComputeUnits
      : null
  } catch (e) {
    console.error('Failed CU simulation', e)
    return null
  }
}

export function isSetComputeLimitInstruction(
  ix: TransactionInstruction,
): boolean {
  return (
    ix.programId.equals(ComputeBudgetProgram.programId) && ix.data[0] === 2 // opcode for setComputeUnitLimit is 2
  )
}
